import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  Cell,
} from 'recharts';
import { Plus, Trash2, Play, DollarSign, TrendingUp, AlertCircle, Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { calcClassWaterfall } from '@/utils/equityMath';

interface ShareholderClass {
  id: string;
  name: string;
  className: string;
  investmentAmount: number;
  shares: number;
  liquidationPreferenceMultiple: number;
  isParticipating: boolean;
  participationCap: number; // Multiple of investment (0 = uncapped)
  seniority: number; // Lower number = senior (paid first)
}

interface ExitScenario {
  id: string;
  name: string;
  exitValue: number;
}

interface WaterfallStep {
  step: number;
  className: string;
  stepName: string;
  amountReceived: number;
  cumulativeReceived: number;
  remainingPool: number;
}

interface ScenarioResult {
  scenarioName: string;
  exitValue: number;
  payouts: {
    className: string;
    seniority: number;
    liquidationPreference: number;
    preferenceReturn: number;
    participationReturn: number;
    totalReturn: number;
    returnMultiple: number;
  }[];
  waterfallSteps: WaterfallStep[];
  commonSharePrice: number;
}

export default function LiquidationWaterfallVisualizer() {
  const [shareholderClasses, setShareholderClasses] = useState<ShareholderClass[]>([
    {
      id: '1',
      name: 'Series B Preferred',
      className: 'Series B',
      investmentAmount: 15000000,
      shares: 5000000,
      liquidationPreferenceMultiple: 1,
      isParticipating: true,
      participationCap: 3,
      seniority: 1,
    },
    {
      id: '2',
      name: 'Series A Preferred',
      className: 'Series A',
      investmentAmount: 5000000,
      shares: 3000000,
      liquidationPreferenceMultiple: 1,
      isParticipating: true,
      participationCap: 0,
      seniority: 2,
    },
    {
      id: '3',
      name: 'Common Stock',
      className: 'Common',
      investmentAmount: 0,
      shares: 12000000,
      liquidationPreferenceMultiple: 0,
      isParticipating: false,
      participationCap: 0,
      seniority: 3,
    },
  ]);

  const [exitScenarios, setExitScenarios] = useState<ExitScenario[]>([
    { id: '1', name: 'Low Exit', exitValue: 15000000 },
    { id: '2', name: 'Base Case', exitValue: 40000000 },
    { id: '3', name: 'High Exit', exitValue: 80000000 },
    { id: '4', name: 'Unicorn Exit', exitValue: 150000000 },
  ]);

  const [results, setResults] = useState<ScenarioResult[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState<string>('');

  const addShareholderClass = () => {
    const newId = String(Date.now());
    setShareholderClasses([
      ...shareholderClasses,
      {
        id: newId,
        name: 'New Class',
        className: 'Series C',
        investmentAmount: 0,
        shares: 0,
        liquidationPreferenceMultiple: 1,
        isParticipating: false,
        participationCap: 0,
        seniority: shareholderClasses.length + 1,
      },
    ]);
  };

  const updateShareholderClass = (
    id: string,
    field: keyof ShareholderClass,
    value: string | number | boolean
  ) => {
    setShareholderClasses(
      shareholderClasses.map((sc) => (sc.id === id ? { ...sc, [field]: value } : sc))
    );
  };

  const removeShareholderClass = (id: string) => {
    setShareholderClasses(shareholderClasses.filter((sc) => sc.id !== id));
  };

  const addExitScenario = () => {
    const newId = String(Date.now());
    setExitScenarios([...exitScenarios, { id: newId, name: 'New Scenario', exitValue: 50000000 }]);
  };

  const updateExitScenario = (id: string, field: keyof ExitScenario, value: string | number) => {
    setExitScenarios(exitScenarios.map((es) => (es.id === id ? { ...es, [field]: value } : es)));
  };

  const removeExitScenario = (id: string) => {
    setExitScenarios(exitScenarios.filter((es) => es.id !== id));
  };

  // The maths lives in calcClassWaterfall so it can be tested. This component used
  // to carry its own copy, which lost a capped class's excess instead of
  // redistributing it, produced negative participation when a cap sat below the
  // preference, and never let non-participating preferred convert to common.
  const calculateWaterfall = (exitValue: number) => {
    const { payouts, steps, commonSharePrice } = calcClassWaterfall(shareholderClasses, exitValue);
    return { payouts, waterfallSteps: steps, commonSharePrice };
  };

  const runAnalysis = () => {
    setAnalyzing(true);
    setTimeout(() => {
      const scenarioResults: ScenarioResult[] = exitScenarios.map((scenario) => {
        const { payouts, waterfallSteps, commonSharePrice } = calculateWaterfall(
          scenario.exitValue
        );
        return {
          scenarioName: scenario.name,
          exitValue: scenario.exitValue,
          payouts,
          waterfallSteps,
          commonSharePrice,
        };
      });

      setResults(scenarioResults);
      if (scenarioResults.length > 0) {
        setSelectedScenario(scenarioResults[0].scenarioName);
      }
      setAnalyzing(false);
    }, 800);
  };

  // Chart data
  const waterfallChartData =
    results.length > 0 && selectedScenario
      ? results
          .find((r) => r.scenarioName === selectedScenario)
          ?.waterfallSteps.map((step) => ({
            step: `Step ${step.step}`,
            stepName: step.stepName,
            amount: step.amountReceived,
            remaining: step.remainingPool,
          }))
      : [];

  const distributionComparisonData = results.map((result) => {
    const data: any = { scenario: result.scenarioName };
    result.payouts.forEach((payout) => {
      data[payout.className] = payout.totalReturn;
    });
    return data;
  });

  const returnMultipleData = results.map((result) => {
    const data: any = { scenario: result.scenarioName };
    result.payouts.forEach((payout) => {
      if (payout.returnMultiple > 0) {
        data[payout.className] = payout.returnMultiple.toFixed(2);
      }
    });
    return data;
  });

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))',
  ];

  const totalInvestment = shareholderClasses.reduce((sum, sc) => sum + sc.investmentAmount, 0);
  const totalShares = shareholderClasses.reduce((sum, sc) => sum + sc.shares, 0);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Liquidation Preference Waterfall Visualizer
          </CardTitle>
          <CardDescription>
            Model detailed payout distributions across shareholder classes and exit scenarios
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Shareholder Classes */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Shareholder Classes</h3>
              <Button onClick={addShareholderClass} size="sm" variant="outline">
                <Plus className="h-4 w-4 mr-1" /> Add Class
              </Button>
            </div>
            <div className="space-y-3">
              {shareholderClasses.map((sc) => (
                <div key={sc.id} className="p-4 border rounded-lg space-y-3">
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-3">
                      <Label className="text-xs">Class Name</Label>
                      <Input
                        value={sc.className}
                        onChange={(e) => updateShareholderClass(sc.id, 'className', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Investment ($)</Label>
                      <Input
                        type="number"
                        value={sc.investmentAmount}
                        onChange={(e) =>
                          updateShareholderClass(sc.id, 'investmentAmount', Number(e.target.value))
                        }
                        className="mt-1"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Shares</Label>
                      <Input
                        type="number"
                        value={sc.shares}
                        onChange={(e) =>
                          updateShareholderClass(sc.id, 'shares', Number(e.target.value))
                        }
                        className="mt-1"
                      />
                    </div>
                    <div className="col-span-1">
                      <Label className="text-xs">Pref Multiple</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={sc.liquidationPreferenceMultiple}
                        onChange={(e) =>
                          updateShareholderClass(
                            sc.id,
                            'liquidationPreferenceMultiple',
                            Number(e.target.value)
                          )
                        }
                        className="mt-1"
                      />
                    </div>
                    <div className="col-span-1">
                      <Label className="text-xs">Seniority</Label>
                      <Input
                        type="number"
                        value={sc.seniority}
                        onChange={(e) =>
                          updateShareholderClass(sc.id, 'seniority', Number(e.target.value))
                        }
                        className="mt-1"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Part. Cap (x)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={sc.participationCap}
                        onChange={(e) =>
                          updateShareholderClass(sc.id, 'participationCap', Number(e.target.value))
                        }
                        className="mt-1"
                        disabled={!sc.isParticipating}
                      />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button
                        onClick={() => removeShareholderClass(sc.id)}
                        size="sm"
                        variant="ghost"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={sc.isParticipating}
                      onCheckedChange={(checked) =>
                        updateShareholderClass(sc.id, 'isParticipating', checked)
                      }
                      id={`participating-${sc.id}`}
                    />
                    <Label htmlFor={`participating-${sc.id}`} className="text-sm cursor-pointer">
                      Participating Preferred{' '}
                      {sc.isParticipating &&
                        sc.participationCap > 0 &&
                        `(${sc.participationCap}x cap)`}
                      {sc.isParticipating && sc.participationCap === 0 && '(Uncapped)'}
                    </Label>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 p-3 bg-muted/50 rounded-lg grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-muted-foreground">Total Investment:</span>
                <span className="ml-2 font-bold">${(totalInvestment / 1000000).toFixed(1)}M</span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Total Shares:</span>
                <span className="ml-2 font-bold">{(totalShares / 1000000).toFixed(1)}M</span>
              </div>
            </div>
          </div>

          {/* Exit Scenarios */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Exit Scenarios</h3>
              <Button onClick={addExitScenario} size="sm" variant="outline">
                <Plus className="h-4 w-4 mr-1" /> Add Scenario
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              {exitScenarios.map((scenario) => (
                <div key={scenario.id} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-5">
                    <Input
                      placeholder="Scenario name"
                      value={scenario.name}
                      onChange={(e) => updateExitScenario(scenario.id, 'name', e.target.value)}
                    />
                  </div>
                  <div className="col-span-6">
                    <Input
                      type="number"
                      placeholder="Exit value"
                      value={scenario.exitValue}
                      onChange={(e) =>
                        updateExitScenario(scenario.id, 'exitValue', Number(e.target.value))
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <Button
                      onClick={() => removeExitScenario(scenario.id)}
                      size="sm"
                      variant="ghost"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-sm">
              <strong>Liquidation Preferences:</strong> Senior classes (lower seniority number) get
              paid first. Participating preferred receives preference + pro-rata share.
              Non-participating chooses preference OR pro-rata (whichever is higher).
              <br />
              <strong>Pari passu:</strong> give two classes the <em>same</em> seniority number and
              they rank equally — if the exit cannot cover both preferences they share what there
              is, pro rata to their claims, rather than one being paid in full ahead of the other.
            </AlertDescription>
          </Alert>

          <Button onClick={runAnalysis} disabled={analyzing} className="w-full">
            <Play className="h-4 w-4 mr-2" />
            {analyzing ? 'Analyzing...' : 'Run Waterfall Analysis'}
          </Button>
        </CardContent>
      </Card>

      {results.length > 0 && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Waterfall Analysis Results</CardTitle>
              <CardDescription>
                Detailed payout distributions across {exitScenarios.length} exit scenarios
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="waterfall">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="waterfall">Waterfall Flow</TabsTrigger>
                  <TabsTrigger value="distribution">Distribution</TabsTrigger>
                  <TabsTrigger value="returns">Return Multiples</TabsTrigger>
                  <TabsTrigger value="details">Detailed Breakdown</TabsTrigger>
                </TabsList>

                <TabsContent value="waterfall" className="space-y-4">
                  <div className="flex gap-2 flex-wrap mb-4">
                    {results.map((result) => (
                      <Button
                        key={result.scenarioName}
                        onClick={() => setSelectedScenario(result.scenarioName)}
                        variant={selectedScenario === result.scenarioName ? 'default' : 'outline'}
                        size="sm"
                      >
                        {result.scenarioName}
                      </Button>
                    ))}
                  </div>

                  {waterfallChartData && waterfallChartData.length > 0 && (
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={waterfallChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="step" />
                        <YAxis
                          label={{ value: 'Amount ($)', angle: -90, position: 'insideLeft' }}
                        />
                        <Tooltip
                          formatter={(value: number) => `$${(value / 1000000).toFixed(2)}M`}
                          labelFormatter={(label, payload) =>
                            payload[0]?.payload?.stepName || label
                          }
                        />
                        <Bar dataKey="amount" fill="hsl(var(--chart-1))" name="Amount Paid">
                          {waterfallChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </TabsContent>

                <TabsContent value="distribution" className="space-y-4">
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={distributionComparisonData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="scenario" />
                      <YAxis label={{ value: 'Payout ($)', angle: -90, position: 'insideLeft' }} />
                      <Tooltip formatter={(value: number) => `$${(value / 1000000).toFixed(2)}M`} />
                      <Legend />
                      {shareholderClasses.map((sc, idx) => (
                        <Bar
                          key={sc.id}
                          dataKey={sc.className}
                          stackId="a"
                          fill={COLORS[idx % COLORS.length]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </TabsContent>

                <TabsContent value="returns" className="space-y-4">
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={returnMultipleData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="scenario" />
                      <YAxis
                        label={{ value: 'Return Multiple (x)', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip />
                      <Legend />
                      {shareholderClasses
                        .filter((sc) => sc.investmentAmount > 0)
                        .map((sc, idx) => (
                          <Line
                            key={sc.id}
                            type="monotone"
                            dataKey={sc.className}
                            stroke={COLORS[idx % COLORS.length]}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                          />
                        ))}
                    </LineChart>
                  </ResponsiveContainer>
                </TabsContent>

                <TabsContent value="details" className="space-y-4">
                  {results.map((result, resultIdx) => (
                    <div key={resultIdx} className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold">{result.scenarioName}</h4>
                        <Badge variant="outline">
                          Exit: ${(result.exitValue / 1000000).toFixed(1)}M
                        </Badge>
                      </div>
                      <div className="rounded-lg border overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-semibold">Class</th>
                              <th className="px-4 py-2 text-right text-sm font-semibold">
                                Liquidation Pref
                              </th>
                              <th className="px-4 py-2 text-right text-sm font-semibold">
                                Preference Return
                              </th>
                              <th className="px-4 py-2 text-right text-sm font-semibold">
                                Participation
                              </th>
                              <th className="px-4 py-2 text-right text-sm font-semibold">
                                Total Return
                              </th>
                              <th className="px-4 py-2 text-right text-sm font-semibold">
                                Return Multiple
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {result.payouts.map((payout, idx) => (
                              <tr key={idx} className="hover:bg-muted/30">
                                <td className="px-4 py-3 font-medium">{payout.className}</td>
                                <td className="px-4 py-3 text-right text-sm">
                                  ${(payout.liquidationPreference / 1000000).toFixed(2)}M
                                </td>
                                <td className="px-4 py-3 text-right font-medium">
                                  ${(payout.preferenceReturn / 1000000).toFixed(2)}M
                                </td>
                                <td className="px-4 py-3 text-right font-medium">
                                  ${(payout.participationReturn / 1000000).toFixed(2)}M
                                </td>
                                <td className="px-4 py-3 text-right font-bold">
                                  ${(payout.totalReturn / 1000000).toFixed(2)}M
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <Badge
                                    variant={
                                      payout.returnMultiple >= 1 ? 'secondary' : 'destructive'
                                    }
                                  >
                                    {payout.returnMultiple > 0
                                      ? `${payout.returnMultiple.toFixed(2)}x`
                                      : '—'}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Key Insights
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {results.map((result, idx) => {
                const preferredPayout = result.payouts
                  .filter((p) => p.liquidationPreference > 0)
                  .reduce((sum, p) => sum + p.totalReturn, 0);
                const commonPayout = result.payouts
                  .filter((p) => p.liquidationPreference === 0)
                  .reduce((sum, p) => sum + p.totalReturn, 0);
                const preferredPct = (preferredPayout / result.exitValue) * 100;
                const commonPct = (commonPayout / result.exitValue) * 100;

                return (
                  <Alert key={idx}>
                    <TrendingUp className="h-4 w-4" />
                    <AlertDescription>
                      <strong>
                        {result.scenarioName} (${(result.exitValue / 1000000).toFixed(1)}M):
                      </strong>
                      <ul className="mt-2 space-y-1 text-sm ml-4 list-disc">
                        <li>
                          Preferred shareholders receive ${(preferredPayout / 1000000).toFixed(2)}M
                          ({preferredPct.toFixed(1)}% of proceeds)
                        </li>
                        <li>
                          Common shareholders receive ${(commonPayout / 1000000).toFixed(2)}M (
                          {commonPct.toFixed(1)}% of proceeds)
                        </li>
                        <li>Implied common share price: ${result.commonSharePrice.toFixed(4)}</li>
                      </ul>
                    </AlertDescription>
                  </Alert>
                );
              })}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
